<template>
  <v-card flat>
    <v-card-text>
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card color="primary" dark elevation="2">
            <v-card-text class="text-center">
              <v-icon large class="mr-2">mdi-doctor</v-icon>
              <span class="text-h6">{{ nombreProfesional }}</span>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="mostrarLeyenda" class="mb-4">
        <v-col cols="12">
          <v-card outlined>
            <v-card-text class="py-2">
              <div class="d-flex flex-wrap justify-center align-center gap-3">
                <div
                  v-if="hayTurnosPresenciales"
                  class="d-flex align-center mr-3"
                >
                  <v-chip small color="success" dark class="mr-2">
                    <v-icon small left>mdi-walk</v-icon>
                  </v-chip>
                  <span class="text-caption">Turno Presencial</span>
                </div>
                <div
                  v-if="hayTurnosVideoconsulta"
                  class="d-flex align-center mr-3"
                >
                  <v-chip small color="purple" dark class="mr-2">
                    <v-icon small left>mdi-video</v-icon>
                  </v-chip>
                  <span class="text-caption">Videoconsulta</span>
                </div>
                <div v-if="hayTurnosEligePaciente" class="d-flex align-center">
                  <v-chip small color="orange" dark class="mr-2">
                    <v-icon small left>mdi-account-question</v-icon>
                  </v-chip>
                  <span class="text-caption">Paciente elige modalidad</span>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        class="mb-4"
      ></v-progress-linear>

      <v-alert
        v-if="sinTurnos && !loading"
        color="orange"
        dark
        prominent
        icon="mdi-calendar-remove"
        class="mb-4"
      >
        <v-row align="center">
          <v-col class="grow">
            <div class="text-h6">Profesional sin turnos disponibles</div>
            <div>
              No hay turnos disponibles en los próximos 3 meses para este
              profesional.
            </div>
          </v-col>
        </v-row>
      </v-alert>

      <v-toolbar
        v-if="!sinTurnos"
        flat
        color="grey lighten-4"
        class="mb-4 rounded toolbar-responsive"
      >
        <v-btn
          icon
          color="primary"
          @click="retrocederSemana"
          :disabled="!puedeRetroceder || loading"
          small
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-spacer></v-spacer>

        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-chip
              color="primary"
              dark
              v-on="on"
              class="px-3 px-sm-6 date-chip"
            >
              <v-icon left small class="d-none d-sm-flex">mdi-calendar</v-icon>
              <span class="date-text">{{ dateFormatted }}</span>
            </v-chip>
          </template>

          <v-date-picker
            v-model="date"
            @input="menu = false"
            locale="es-ar"
            :first-day-of-week="1"
            @change="cargarTurnos"
            :allowed-dates="allowedDates"
            :min="fechaMinima"
            :max="fechaMaxima"
            color="primary"
          ></v-date-picker>
        </v-menu>

        <v-spacer></v-spacer>

        <v-btn
          icon
          color="primary"
          @click="avanzarSemana"
          :disabled="date >= fechaMaxima || loading"
          small
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-toolbar>

      <v-row v-if="!sinTurnos && !loading">
        <v-col
          v-for="(turnos, dia) in turnosPorDia"
          :key="dia"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card elevation="3" class="day-card" hover>
            <v-card-title class="primary white--text py-3">
              <v-icon left dark>mdi-calendar-today</v-icon>
              <div>
                <div class="text-subtitle-1">{{ getDiaSemana(dia) }}</div>
                <div class="text-caption">{{ dia }}</div>
              </div>
            </v-card-title>

            <v-card-text class="pa-2">
              <v-chip-group column>
                <v-chip
                  v-for="turno in turnos"
                  :key="turno.id"
                  :color="getColorTurno(turno.tipoTurno)"
                  outlined
                  class="ma-1 time-chip"
                  @click="seleccionarTurno(turno)"
                >
                  <v-icon left small>{{
                    getIconoTurno(turno.tipoTurno)
                  }}</v-icon>
                  {{ turno.hora }}
                </v-chip>
              </v-chip-group>

              <v-alert
                v-if="turnos.length === 0"
                color="grey"
                text
                dense
                class="ma-2"
              >
                Sin turnos disponibles
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions>
      <v-btn color="primary" outlined @click="$emit('volver')">
        <v-icon left>mdi-arrow-left</v-icon>
        Volver
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn v-if="sinTurnos" color="primary" @click="$emit('volver')">
        Elegir otro profesional
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.day-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.day-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
}

.time-chip {
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.time-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.rounded {
  border-radius: 8px;
}

.date-text {
  font-size: 0.875rem;
}

.gap-3 {
  gap: 12px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .time-chip {
    font-size: 0.75rem;
  }

  .date-text {
    font-size: 0.75rem;
  }

  .date-chip {
    height: 28px !important;
  }

  .toolbar-responsive {
    padding: 4px 8px !important;
  }
}

@media (max-width: 400px) {
  .time-chip {
    font-size: 0.7rem;
    padding: 4px 8px;
  }

  .date-text {
    font-size: 0.7rem;
  }

  .date-chip {
    height: 26px !important;
    padding: 0 8px !important;
  }
}
</style>

<script>
import axios from "../axios-auth.js";
import moment from "moment";

export default {
  name: "GestionTurnos",
  props: {
    idInstitucion: {
      type: Number,
      required: true,
    },
    idProfesional: {
      type: Number,
      required: true,
    },
    nombreProfesional: {
      type: String,
      required: true,
    },
    idObraSocial: {
      type: Number,
      required: true,
    },
    tipoDeTurno: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      date: null,
      dateFormatted: null,
      menu: false,
      listaTurnos: [],
      loading: false,
      sinTurnos: false,
      fechaMinima: moment().format("YYYY-MM-DD"),
      fechaMaxima: moment().add(3, "months").format("YYYY-MM-DD"),
    };
  },
  computed: {
    turnosPorDia() {
      const grupos = {};

      // Obtener todos los días de la semana actual (Lunes a Domingo)
      const inicioSemana = moment(this.date);
      for (let i = 0; i < 7; i++) {
        const dia = moment(inicioSemana).add(i, "days").format("DD/MM/YYYY");
        grupos[dia] = [];
      }

      // Agrupar turnos por día
      this.listaTurnos.forEach((turno) => {
        const dia = turno.fechaTurnoString;
        if (grupos[dia]) {
          grupos[dia].push(turno);
        }
      });

      // Convertir a array y ordenar para que Lunes sea primero
      const gruposArray = Object.entries(grupos);
      gruposArray.sort((a, b) => {
        const [dA, mA, yA] = a[0].split("/");
        const [dB, mB, yB] = b[0].split("/");
        const dateA = new Date(yA, mA - 1, dA);
        const dateB = new Date(yB, mB - 1, dB);
        return dateA - dateB;
      });

      // Convertir de vuelta a objeto
      const gruposOrdenados = {};
      gruposArray.forEach(([dia, turnos]) => {
        gruposOrdenados[dia] = turnos;
      });

      return gruposOrdenados;
    },
    cantidadIntervalosRequeridos() {
      // Si hay tipo de turno y tiene duración, usarla; sino 1 por defecto
      if (this.tipoDeTurno && this.tipoDeTurno.duracion) {
        return this.tipoDeTurno.duracion;
      }
      return 1;
    },
    hayTurnosPresenciales() {
      return this.listaTurnos.some(
        (turno) => !turno.tipoTurno || turno.tipoTurno === 0,
      );
    },
    hayTurnosVideoconsulta() {
      return this.listaTurnos.some((turno) => turno.tipoTurno === 1);
    },
    hayTurnosEligePaciente() {
      return this.listaTurnos.some((turno) => [2, 3].includes(turno.tipoTurno));
    },
    mostrarLeyenda() {
      return (
        this.hayTurnosPresenciales ||
        this.hayTurnosVideoconsulta ||
        this.hayTurnosEligePaciente
      );
    },
    puedeRetroceder() {
      // Verificar si retroceder una semana nos llevaría antes de la fecha mínima
      const semanaAnterior = moment(this.date).subtract(7, "days");
      return semanaAnterior.isSameOrAfter(moment(this.fechaMinima));
    },
  },
  mounted() {
    this.buscarPrimeraSemanaConTurnos();
  },
  watch: {
    date(newDate) {
      if (newDate) {
        this.dateFormatted = moment(newDate).format("DD/MM/YYYY");
      }
    },
  },
  methods: {
    setToday() {
      const hoy = new Date().getDay();
      const diasARestar = hoy === 0 ? 6 : hoy - 1;
      const dias = 1000 * 60 * 60 * 24 * diasARestar;
      const resta = new Date().getTime() - dias;
      this.date = moment(resta).format("YYYY-MM-DD");
      this.dateFormatted = moment(resta).format("DD/MM/YYYY");
    },
    getDiaSemana(fechaStr) {
      const [d, m, y] = fechaStr.split("/");
      const date = new Date(y, m - 1, d);
      const dias = [
        "Domingo",
        "Lunes",
        "Martes",
        "Miércoles",
        "Jueves",
        "Viernes",
        "Sábado",
      ];
      return dias[date.getDay()];
    },
    retrocederSemana() {
      this.date = moment(this.date).subtract(7, "days").format("YYYY-MM-DD");
      this.cargarTurnos();
    },
    avanzarSemana() {
      this.date = moment(this.date).add(7, "days").format("YYYY-MM-DD");
      this.cargarTurnos();
    },
    allowedDates(val) {
      // Usar moment para evitar problemas de zona horaria
      // getDay() retorna: 0=Domingo, 1=Lunes, 2=Martes, etc.
      return moment(val).day() === 1; // Solo lunes
    },
    async buscarPrimeraSemanaConTurnos() {
      this.loading = true;
      this.sinTurnos = false;
      this.setToday();

      let fechaActual = moment(this.date);
      const fechaLimite = moment(this.fechaMaxima);

      while (fechaActual.isSameOrBefore(fechaLimite)) {
        try {
          const response = await axios.get(
            "/PortalPublico/ArmarTurnosSemanaPortalOptimizado",
            {
              params: {
                idProfesional: this.idProfesional,
                fecha: fechaActual.format("YYYY-MM-DD"),
                idInstitucion: this.idInstitucion,
                idObraSocial: this.idObraSocial,
                cantidadIntervalosRequeridos: this.cantidadIntervalosRequeridos,
              },
            },
          );

          const turnosDisponibles = response.data.filter(
            (turno) => turno.paciente === "Disponible",
          );

          if (turnosDisponibles.length > 0) {
            this.date = fechaActual.format("YYYY-MM-DD");
            this.dateFormatted = fechaActual.format("DD/MM/YYYY");
            this.listaTurnos = turnosDisponibles;
            this.loading = false;
            return;
          }

          // Avanzar una semana
          fechaActual.add(7, "days");
        } catch (error) {
          console.error("Error al buscar turnos:", error);
          this.loading = false;
          this.$swal.fire({
            icon: "error",
            title: "Error al cargar turnos",
            showConfirmButton: false,
            timer: 2000,
          });
          return;
        }
      }

      // No se encontraron turnos en todo el período
      this.sinTurnos = true;
      this.loading = false;
    },
    cargarTurnos() {
      this.loading = true;
      axios
        .get("/PortalPublico/ArmarTurnosSemanaPortalOptimizado", {
          params: {
            idProfesional: this.idProfesional,
            fecha: this.date,
            idInstitucion: this.idInstitucion,
            idObraSocial: this.idObraSocial,
            cantidadIntervalosRequeridos: this.cantidadIntervalosRequeridos,
          },
        })
        .then((response) => {
          this.listaTurnos = response.data.filter(
            (turno) => turno.paciente === "Disponible",
          );
        })
        .catch((error) => {
          console.error("Error al cargar turnos:", error);
          this.$swal.fire({
            icon: "error",
            title: "Error al cargar turnos",
            showConfirmButton: false,
            timer: 2000,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    seleccionarTurno(turno) {
      this.$emit("turnoSeleccionado", turno);
    },
    getColorTurno(tipoTurno) {
      // 0 o null/undefined = Presencial (verde)
      // 1 = Videoconsulta (morado)
      // 2 o 3 = Paciente elige (naranja)
      if (!tipoTurno || tipoTurno === 0) {
        return "success";
      } else if (tipoTurno === 1) {
        return "purple";
      } else if ([2, 3].includes(tipoTurno)) {
        return "orange";
      }
      return "success"; // default
    },
    getIconoTurno(tipoTurno) {
      if (!tipoTurno || tipoTurno === 0) {
        return "mdi-walk";
      } else if (tipoTurno === 1) {
        return "mdi-video";
      } else if ([2, 3].includes(tipoTurno)) {
        return "mdi-account-question";
      }
      return "mdi-clock-outline"; // default
    },
  },
};
</script>
